/**
 * Viewer mock layer (Track 0).
 *
 * When `REACT_APP_VIEWER_MOCK === "true"`, this module overrides the methods on
 * the existing `bim*Api` objects with fixture-returning stubs. Subsequent tracks
 * (Track 1 tiles, Track 3 BCF, Track 4 AI) extend this file.
 *
 * Call `installViewerMocks()` once at app bootstrap (src/index.tsx). The
 * function no-ops when the env flag is off, so it's safe to leave wired.
 */
import { bimModelApi, bimViewerApi, bimClashApi, bimAiApi } from "../../../api/bim/bimService";
import {
  mockModel,
  mockProjectModels,
  mockVersions,
  mockSpatialTree,
  mockClassificationSummary,
  mockTiles,
  mockTileStatus,
  mockAnnotations,
  mockElementDetail,
  mockClashes,
  mockClashSummary,
  mockCopilotResponse,
} from "./fixtures";

/** Build an axios-shaped response so the existing unwrapGateway helper finds the payload. */
const ok = <T>(payload: T) =>
  Promise.resolve({
    data: { status: "OK", statusCode: 200, message: "mock", data: payload },
    status: 200,
    statusText: "OK",
    headers: {},
    config: {} as any,
  });

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));
const okSlow = async <T>(payload: T, ms = 240) => {
  await delay(ms);
  return (await ok(payload)) as any;
};

let installed = false;

export function installViewerMocks(): void {
  if (process.env.REACT_APP_VIEWER_MOCK !== "true") return;
  if (installed) return;
  installed = true;

  // ---------- Model ----------
  bimModelApi.getModel = ((_modelId: number) => okSlow(mockModel)) as any;
  bimModelApi.listModels = ((_params: any) => okSlow(mockProjectModels)) as any;
  bimModelApi.getVersions = ((_modelId: number) => okSlow(mockVersions)) as any;
  bimModelApi.compareVersions = ((_data: any) =>
    okSlow({
      added: 24, removed: 12, modified: 8, unchanged: mockModel.totalElements - 44,
      addedElements: [], removedElements: [], modifiedElements: [],
    })) as any;

  // ---------- Viewer ----------
  bimViewerApi.getTiles = ((_modelId: number) => okSlow(mockTiles)) as any;
  bimViewerApi.getTileStatus = ((_modelId: number) => okSlow(mockTileStatus)) as any;
  bimViewerApi.getSpatialTree = ((_modelId: number) => okSlow(mockSpatialTree)) as any;
  bimViewerApi.getAnnotations = ((_modelId: number) => okSlow(mockAnnotations)) as any;
  bimViewerApi.createAnnotation = ((data: any) =>
    okSlow({ ...data, id: Date.now() })) as any;
  bimViewerApi.deleteAnnotation = ((_id: number) => okSlow({ deleted: true })) as any;
  bimViewerApi.getViewStates = ((_modelId: number) => okSlow([])) as any;
  bimViewerApi.saveViewState = ((data: any) =>
    okSlow({ ...data, viewStateId: Date.now() })) as any;
  bimViewerApi.deleteViewState = ((_id: number) => okSlow({ deleted: true })) as any;
  bimViewerApi.generateTiles = ((_modelId: number) =>
    okSlow({ status: "queued" })) as any;

  // ---------- Clash ----------
  bimClashApi.getClashResults = ((_params: any) => okSlow(mockClashes)) as any;
  bimClashApi.getClashSummary = ((_projectId: any) => okSlow(mockClashSummary)) as any;
  bimClashApi.resolveClash = ((clashId: any) => okSlow({ clashId, status: "resolved" })) as any;
  bimClashApi.ignoreClash = ((clashId: any) => okSlow({ clashId, status: "ignored" })) as any;

  // ---------- AI ----------
  // Existing bimAiApi already has a `chat` method; we override it with the
  // fixture-driven copilot response.
  (bimAiApi as any).chat = (payload: { question: string; context?: any }) =>
    okSlow(mockCopilotResponse(payload?.question || ""));

  // Element detail isn't on bimAiApi/Viewer; expose globally so Track 2's
  // properties panel + ⌘K selection have something to render without a network call.
  (window as any).__VIEWER_MOCK__ = {
    elementDetail: mockElementDetail,
    classificationSummary: mockClassificationSummary,
    projectId: mockModel.projectId,
    modelId: mockModel.modelId,
  };

  // eslint-disable-next-line no-console
  console.info(
    "%c[viewer-mock] enabled — REACT_APP_VIEWER_MOCK=true",
    "color:#2263b3;font-weight:bold;"
  );
}
